import { getCategoryBreadcrumbTrail } from './categoryTree';
import { findCategoryBySlugOrId, getCategoryUrl } from './urls';

export const HOME_CRUMB = { label: 'Головна', path: '/' };
export const CATALOG_CRUMB = { label: 'Каталог', path: '/' };

const ACCOUNT_CRUMB = { label: 'Особистий кабінет', path: '/profile' };

const humanizeSlug = (value) => {
  const slug = String(value ?? '').trim();
  if (!slug || /^\d+$/.test(slug)) {
    return '';
  }

  return slug
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w|\s\w/g, (char) => char.toUpperCase());
};

const mapCategoryTrail = (categories, categoryId) =>
  getCategoryBreadcrumbTrail(categories, categoryId).map((category) => ({
    label: category.name,
    path: category.path,
  }));

const buildSearchTrail = (search) => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const query = params.get('q')?.trim();

  if (!query) {
    return [HOME_CRUMB, { label: 'Пошук' }];
  }

  return [
    HOME_CRUMB,
    { label: 'Пошук', path: '/search' },
    { label: `«${query}»` },
  ];
};

const STATIC_TRAILS = {
  '/': [{ label: 'Головна' }],
  '/cart': [HOME_CRUMB, { label: 'Кошик' }],
  '/checkout': [
    HOME_CRUMB,
    { label: 'Кошик', path: '/cart' },
    { label: 'Оформлення замовлення' },
  ],
  '/profile': [HOME_CRUMB, { label: 'Особистий кабінет' }],
  '/favorites': [HOME_CRUMB, { label: 'Обране' }],
  '/orders': [HOME_CRUMB, ACCOUNT_CRUMB, { label: 'Мої замовлення' }],
  '/delivery': [HOME_CRUMB, { label: 'Доставка' }],
  '/payment': [HOME_CRUMB, { label: 'Доставка і оплата', path: '/delivery' }, { label: 'Оплата' }],
};

/**
 * @param {{
 *   pathname: string,
 *   search?: string,
 *   categories?: Array,
 *   categoryId?: number|null,
 *   slugOrId?: string,
 *   product?: object|null,
 *   productLoading?: boolean,
 *   categoriesLoading?: boolean,
 *   pageTrail?: Array<{label: string, path?: string}>|null,
 * }} params
 * @returns {Array<{label: string, path?: string}>|null}
 */
export const buildBreadcrumbTrail = ({
  pathname,
  search = '',
  categories = [],
  categoryId = null,
  slugOrId = '',
  product = null,
  productLoading = false,
  categoriesLoading = false,
  pageTrail = null,
}) => {
  if (pathname === '/') {
    return STATIC_TRAILS['/'];
  }

  if (pageTrail?.length) {
    if (/^\/categories\/[^/]+/.test(pathname)) {
      return [HOME_CRUMB, CATALOG_CRUMB, ...pageTrail];
    }
    if (/^\/products\/[^/]+/.test(pathname)) {
      return [HOME_CRUMB, CATALOG_CRUMB, ...pageTrail];
    }
  }

  if (/^\/categories\/[^/]+/.test(pathname)) {
    const categoryTrail = mapCategoryTrail(categories, categoryId);

    if (categoryTrail.length) {
      return [HOME_CRUMB, CATALOG_CRUMB, ...categoryTrail];
    }

    if (categoriesLoading || (slugOrId && !categories.length)) {
      return [HOME_CRUMB, CATALOG_CRUMB, { label: 'Завантаження…' }];
    }

    const category = findCategoryBySlugOrId(categories, slugOrId);
    if (category?.name) {
      return [
        HOME_CRUMB,
        CATALOG_CRUMB,
        { label: category.name, path: getCategoryUrl(category) },
      ];
    }

    const fallbackLabel = humanizeSlug(slugOrId) || 'Категорія';
    const fallbackPath = slugOrId ? `/categories/${slugOrId}` : undefined;

    return [
      HOME_CRUMB,
      CATALOG_CRUMB,
      { label: fallbackLabel, path: fallbackPath },
    ];
  }

  if (/^\/products\/[^/]+/.test(pathname)) {
    if (productLoading || (!product && slugOrId)) {
      return [HOME_CRUMB, CATALOG_CRUMB, { label: 'Завантаження…' }];
    }

    if (!product) {
      return [HOME_CRUMB, CATALOG_CRUMB, { label: 'Товар не знайдено' }];
    }

    const categoryTrail = mapCategoryTrail(categories, product.category?.id);

    return [
      HOME_CRUMB,
      CATALOG_CRUMB,
      ...categoryTrail,
      { label: product.title },
    ];
  }

  if (pathname === '/search') {
    return buildSearchTrail(search);
  }

  if (STATIC_TRAILS[pathname]) {
    return STATIC_TRAILS[pathname];
  }

  return [HOME_CRUMB, { label: 'Сторінку не знайдено' }];
};
